import React, { useState } from "react";
import { Box, Grid, Stack, Typography, CircularProgress } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";

const ItemChart = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "transparent",
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
  boxShadow: "none",
}));

export const KpiDonutChart = ({
  title,
  totalLabel,
  totalValue,
  series,
  colors = ["255, 76, 134", "35, 189, 71"], // RGB values: #FF4C86, #23BD47
  labels = ["NOT CONVERTED", "CONVERTED"],
  loading = false,
}) => {
  const total = series.reduce((a, b) => a + b, 0);
  const convertedPercent =
    total > 0 ? ((series[1] * 100) / total).toFixed(2) : 0;

  const data1 = [
    {
      label: labels[0],
      value: series[0],
      color: `rgb(${colors[0]})`,
      bg: `rgba(${colors[0]}, 0.1)`,
    },
    {
      label: labels[1],
      value: series[1],
      color: `rgb(${colors[1]})`,
      bg: `rgba(${colors[1]}, 0.1)`,
    },
  ].filter((item) => item.value > 0);

  const hasData = total > 0 && data1.length > 0;
  const [activeIndex, setActiveIndex] = useState(-1);

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(-1);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: data.color,
            color: "#fff",
            padding: "5px 10px",
            borderRadius: "4px",
            fontSize: "12px",
            fontWeight: 500,
          }}
        >
          {`${data.label}: ${data.value}`}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box sx={{ padding: "10px 20px", position: "relative" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Typography
          variant="div"
          component="div"
          fontSize={"18px"}
          fontWeight="600"
          color={"#2B3033"}
        >
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontSize="14px">
          {totalLabel} - {totalValue}
        </Typography>
      </Box>
      <ItemChart
        className="DEF_ratio_chart"
        sx={{ opacity: loading ? 0.5 : 1 }}
      >
        {hasData ? (
          <Grid
            container
            columnSpacing={2}
            sx={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <Grid
              item
              xs={6}
              md={7}
              lg={7}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  maxWidth: "250px",
                  height: "250px",
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <defs>
                      <filter
                        id="shadow"
                        x="-50%"
                        y="-50%"
                        width="200%"
                        height="200%"
                      >
                        <feDropShadow
                          dx="0"
                          dy="0"
                          stdDeviation="5"
                          floodColor="#000"
                          floodOpacity="0.3"
                        />
                      </filter>
                    </defs>
                    <Pie
                      data={data1}
                      cx="50%"
                      cy="50%"
                      innerRadius={75}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      startAngle={90}
                      endAngle={-270}
                      onMouseEnter={onPieEnter}
                      onMouseLeave={onPieLeave}
                    >
                      {data1.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.bg}
                          stroke={entry.color}
                          strokeWidth={2}
                          filter={
                            index === activeIndex ? "url(#shadow)" : "none"
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    pointerEvents: "none",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "#373d3f",
                      lineHeight: 1.2,
                      mb: 0.5,
                    }}
                  >
                    Total
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "18px",
                      fontWeight: 600,
                      color: "#373d3f",
                      lineHeight: 1.2,
                      letterSpacing: "0.05px",
                    }}
                  >
                    {convertedPercent}%
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6} md={5} lg={5} textAlign={"left"}>
              <Box className="infoCart_scroll">
                {data1.map((el, idx) => (
                  <Box key={idx} sx={{ py: 0.5, px: 1, mb: 0.3 }}>
                    <Typography
                      className="label_leads_state"
                      variant="span"
                      component="div"
                      textTransform={"uppercase"}
                      sx={{
                        fontSize: "12px",
                        color: el.color,
                        fontWeight: 500,
                      }}
                    >
                      {el.label}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={5}
                      sx={{ justifyContent: "space-between" }}
                    >
                      <Typography
                        className="val_leads_state"
                        variant="span"
                        component="div"
                        sx={{
                          color: el.color,
                          fontSize: "14px",
                          fontWeight: 600,
                        }}
                      >
                        {el.value}
                      </Typography>
                    </Stack>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "300px",
              py: 4,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </ItemChart>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backdropFilter: "blur(2px)",
            backgroundColor: "rgba(255,255,255,0.6)",
            borderRadius: "8px",
            zIndex: 2,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};
